import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Shield, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { toast } from "sonner";

const ROLE_DESC = { admin: "Owner / Super Admin — full access", executive: "Executive — dashboards, AI Brain, approvals", manager: "Manager — approvals, CRM, scheduling, recruiting", staff: "Staff — CRM, recruiting, scheduling (no approvals/Brain)", user: "Standard user (family/caregiver)" };

export default function Team() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [team, setTeam] = useState([]);
  const [roles, setRoles] = useState([]);

  const load = () => api.get("/ops/team").then((r) => { setTeam(r.data.team); setRoles([...r.data.roles, "user"]); }).catch(() => toast.error("Owner access required"));
  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (user.role !== "admin") { navigate("/ops"); return; }
    load();
  }, [user, loading, navigate]);

  const setRole = async (email, role) => {
    try { await api.post("/ops/team/role", { email, role }); toast.success(`${email} is now ${role}`); load(); }
    catch { toast.error("Could not update role"); }
  };

  if (loading || !user) return <div className="py-32 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="team-page">
      <div className="flex items-center gap-2 mb-1"><Shield className="w-5 h-5 text-teal-500" /><p className="text-xs uppercase tracking-[0.16em] text-teal-600 font-semibold">Roles & Permissions</p></div>
      <h1 className="font-display text-3xl lg:text-4xl font-bold text-blue-950">T5 Team</h1>
      <p className="text-slate-600 mt-1">Control who can access the Operations Center. Owner &gt; Executive &gt; Manager &gt; Staff.</p>

      <div className="mt-6 space-y-3">
        {team.map((t) => (
          <div key={t.id} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`team-member-${t.email}`}>
            <div className="flex items-center gap-3">
              <span className="grid place-items-center w-10 h-10 rounded-full bg-blue-950 text-white"><Users className="w-5 h-5" /></span>
              <div><p className="font-semibold text-blue-950">{t.name || t.email}</p><p className="text-xs text-slate-500">{t.email} · {ROLE_DESC[t.role]}</p></div>
            </div>
            <select value={t.role} onChange={(e) => setRole(t.email, e.target.value)} disabled={t.email === user.email}
              data-testid={`team-role-${t.email}`} className="h-9 rounded-lg border border-slate-200 px-3 text-sm capitalize disabled:opacity-50">
              {roles.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        ))}
      </div>
      <p className="mt-6 text-xs text-slate-400">To add a teammate: have them register a normal account, then set their role here.</p>
    </div>
  );
}
